import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog as ScrollableDialog,
  ScrollableDialogContent,
  DialogHeader as ScrollableDialogHeader,
  DialogTitle as ScrollableDialogTitle,
  DialogFooter as ScrollableDialogFooter,
} from '@/components/ui/scrollable-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, X, Save, Image as ImageIcon, Package, Tag, Layers, Upload } from 'lucide-react';
import { shopifyApi, getCurrencySymbol } from '@/lib/api/services/shopify';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { ToastAction } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

// Maps backend `inventoryFailures[].reason` codes (set in
// ShopifyConnectorService.updateInventoryLevels) to a customer-friendly
// title + description. The raw backend `detail` field carries technical
// strings like "missing read_inventory permission" — useful for support
// logs (we still write them to the console) but they confuse end users
// who don't know what an OAuth scope is. This mapper is the single place
// where we translate to plain English.
type InventoryFailureReason =
  | 'missing_shopify_scope'
  | 'shopify_unauthorized'
  | 'no_shopify_location'
  | 'missing_inventory_item_id'
  | 'shopify_rejected'
  | 'network_error'
  | string;

interface FriendlyFailureMessage {
  title: string;
  description: string;
  /** When true, the toast shows a "Reconnect Shopify" CTA pointing at /home/shopify. */
  offerReconnect?: boolean;
}

const mapInventoryFailureToFriendlyMessage = (
  reason: InventoryFailureReason,
  failureCount: number,
): FriendlyFailureMessage => {
  const variantWord = failureCount > 1 ? `${failureCount} variants` : 'this variant';
  switch (reason) {
    case 'missing_shopify_scope':
      return {
        title: "Couldn't update stock on Shopify",
        description:
          `Your Shopify connection isn't allowed to change inventory. Other product details were saved. ` +
          `Reconnect Shopify with full access and try again.`,
        offerReconnect: true,
      };
    case 'shopify_unauthorized':
      return {
        title: 'Shopify connection expired',
        description:
          `Your Shopify session has expired, so stock for ${variantWord} couldn't be updated. ` +
          `Reconnect Shopify and try again. Other product details were saved.`,
        offerReconnect: true,
      };
    case 'no_shopify_location':
      return {
        title: "Couldn't update stock on Shopify",
        description:
          `Your Shopify store needs at least one location set up to track inventory. ` +
          `Open Shopify Admin → Settings → Locations, then try again. Other product details were saved.`,
      };
    case 'missing_inventory_item_id':
      return {
        title: "Can't track stock for this product",
        description:
          `Shopify isn't tracking inventory on ${variantWord}. Other product details were saved.`,
      };
    case 'network_error':
      return {
        title: "Couldn't reach Shopify",
        description:
          `Stock changes weren't sent because we couldn't connect to Shopify. ` +
          `Check your internet and try again. Other product details were saved.`,
      };
    case 'shopify_rejected':
    default:
      return {
        title: "Stock didn't sync to Shopify",
        description:
          `Shopify didn't accept the stock change for ${variantWord}. Other product details were saved. ` +
          `Try again, or contact support if this keeps happening.`,
      };
  }
};

interface EditShopifyProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: any | null;
  selectedAccountId: string | null;
  /** Currency of the connected Shopify store (e.g. 'USD', 'INR'). Drives the
   *  price-field symbol. Defaults to 'USD' when the connection currency is
   *  unknown. */
  shopCurrency?: string;
  onSuccess: () => void;
}

interface ImageEntry {
  preview: string;
  base64?: string;      // set for newly uploaded files
  existingId?: number;  // set for images already on Shopify
  variantIds?: number[];
}

interface Variant {
  id?: number;
  option1: string;
  price: string;
  inventory_quantity: number | '';
  image: ImageEntry | null;
}

const STATUS_CONFIG = {
  active:   { label: 'Active',   color: 'bg-emerald-500' },
  draft:    { label: 'Draft',    color: 'bg-slate-400' },
  archived: { label: 'Archived', color: 'bg-orange-400' },
};

const parseJson = (field: any): any[] => {
  if (!field) return [];
  if (typeof field === 'string') { try { return JSON.parse(field); } catch { return []; } }
  return Array.isArray(field) ? field : [];
};

const parseTags = (raw: any): string[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === 'string') {
    if (raw.startsWith('[')) { try { return JSON.parse(raw).filter(Boolean); } catch {} }
    return raw.split(',').map(t => t.trim()).filter(Boolean);
  }
  return [];
};

export const EditShopifyProductDialog: React.FC<EditShopifyProductDialogProps> = ({
  open, onOpenChange, product, selectedAccountId, shopCurrency = 'USD', onSuccess,
}) => {
  const navigate = useNavigate();
  const currencySymbol = getCurrencySymbol(shopCurrency);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [productType, setProductType] = useState('');
  const [status, setStatus] = useState('active');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [variants, setVariants] = useState<Variant[]>([]);
  const [mainImages, setMainImages] = useState<ImageEntry[]>([]);
  const [dragging, setDragging] = useState(false);

  // Store meta
  const [vendors, setVendors] = useState<string[]>([]);
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [metaLoading, setMetaLoading] = useState(false);

  const mainFileInputRef = useRef<HTMLInputElement>(null);
  const variantFileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const tagInputRef = useRef<HTMLInputElement>(null);

  // Populate form when product changes
  useEffect(() => {
    if (!product) return;
    setTitle(product.title || '');
    setDescription(product.description || product.body_html || '');
    setVendor(product.vendor || '');
    setProductType(product.product_type || '');
    setStatus(product.status || 'active');
    setTags(parseTags(product.tags));
    setTagInput('');

    const parsedImages: any[] = parseJson(product.images);
    const parsedVariants: any[] = parseJson(product.variants);

    // Only show images that are NOT linked to a specific variant in the main grid
    setMainImages(
      parsedImages
        .filter(img => !img.variant_ids || img.variant_ids.length === 0)
        .map(img => ({ preview: img.src, existingId: img.id, variantIds: [] }))
    );

    setVariants(
      parsedVariants.length > 0
        ? parsedVariants.map(v => {
            const variantImg = v.image_id
              ? parsedImages.find(img => String(img.id) === String(v.image_id))
              : null;
            return {
              id: v.id,
              option1: v.option1 || '',
              price: v.price || '',
              inventory_quantity: v.inventory_quantity ?? 0,
              image: variantImg
                ? { preview: variantImg.src, existingId: variantImg.id }
                : null,
            };
          })
        : [{ option1: '', price: '', inventory_quantity: 0, image: null }]
    );
  }, [product]);

  // Load store meta
  useEffect(() => {
    if (!open || !selectedAccountId) return;
    setMetaLoading(true);
    shopifyApi.getStoreMeta(selectedAccountId)
      .then(meta => { setVendors(meta.vendors || []); setProductTypes(meta.productTypes || []); })
      .finally(() => setMetaLoading(false));
  }, [open, selectedAccountId]);

  // ── File reader ──────────────────────────────────────────────────────────────
  const readFile = (file: File): Promise<ImageEntry> =>
    new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) { reject(new Error('Not an image')); return; }
      if (file.size > 20 * 1024 * 1024) { reject(new Error(`${file.name} exceeds 20MB`)); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const r = reader.result as string;
        resolve({ preview: r, base64: r.split(',')[1] });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // ── Main images ──────────────────────────────────────────────────────────────
  const addMainFiles = async (files: FileList | null) => {
    if (!files) return;
    const entries = await Promise.all(
      Array.from(files).map(f => readFile(f).catch(err => { toast({ title: err.message, variant: 'destructive' }); return null; }))
    );
    setMainImages(prev => [...prev, ...(entries.filter(Boolean) as ImageEntry[])]);
  };

  // ── Tags ─────────────────────────────────────────────────────────────────────
  const addTag = (raw: string) => {
    const t = raw.trim().replace(/,+$/, '');
    if (!t || tags.includes(t)) return;
    setTags(prev => [...prev, t]);
    setTagInput('');
  };
  const onTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); }
    else if (e.key === 'Backspace' && !tagInput && tags.length) setTags(prev => prev.slice(0, -1));
  };

  // ── Variants ─────────────────────────────────────────────────────────────────
  const updateVariant = (index: number, field: keyof Variant, value: any) =>
    setVariants(prev => { const next = [...prev]; next[index] = { ...next[index], [field]: value }; return next; });

  const setVariantImage = async (index: number, files: FileList | null) => {
    if (!files?.[0]) return;
    try { updateVariant(index, 'image', await readFile(files[0])); }
    catch (err: any) { toast({ title: err.message, variant: 'destructive' }); }
  };

  const addVariant = () => { setVariants(prev => [...prev, { option1: '', price: '', inventory_quantity: 0, image: null }]); };
  const removeVariant = (i: number) => { if (variants.length > 1) setVariants(prev => prev.filter((_, idx) => idx !== i)); };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!product || !selectedAccountId) return;

    setLoading(true);
    try {
      const newMainImages = mainImages.filter(img => img.base64).map(img => ({ attachment: img.base64 }));
      const retainedImages = mainImages.filter(img => img.existingId && !img.base64).map(img => ({ id: img.existingId }));

      const variantImagesPayload = variants
        .map(v => {
          if (!v.image || !v.id) return null;
          if (v.image.base64) {
            // New upload — send as attachment
            return { attachment: v.image.base64, variant_ids: [v.id] };
          } else if (v.image.existingId) {
            // Existing image — retain by id and re-assert variant_ids so the association is preserved
            return { id: v.image.existingId, variant_ids: [v.id] };
          }
          return null;
        })
        .filter(Boolean);

      const productData: any = {
        title,
        body_html: description,
        vendor: vendor === '__custom__' ? '' : vendor,
        product_type: productType === '__custom__' ? '' : productType,
        status,
        tags: tags.join(', '),
        variants: variants.map(v => ({
          ...(v.id ? { id: v.id } : {}),
          option1: v.option1 || 'Default Title',
          price: String(v.price),
          inventory_quantity: v.inventory_quantity === '' ? 0 : Number(v.inventory_quantity),
        })),
      };

      // Combine main images + variant images into one call to avoid Shopify
      // replacing all images when variant images are sent in a separate request.
      const originalImageCount = parseJson(product.images).length;
      const hasNewMainImages = newMainImages.length > 0;
      const removedExisting = mainImages.filter(img => img.existingId).length !== originalImageCount;

      if (hasNewMainImages || removedExisting || variantImagesPayload.length) {
        productData.images = [
          ...newMainImages,          // new main image uploads
          ...retainedImages,         // existing main images to keep (by id)
          ...variantImagesPayload,   // new variant images with variant_ids
        ];
      }

      const result: any = await shopifyApi.updateProduct(selectedAccountId, product.shopify_product_id, productData);

      const inventoryFailures: Array<{ variantId: string; reason: string; detail?: string }> =
        Array.isArray(result?.inventoryFailures) ? result.inventoryFailures : [];

      if (inventoryFailures.length > 0) {
        // Preserve the raw backend detail in the console so support can see
        // the actual scope name / Shopify response when a customer reports
        // the issue. The user-facing toast is intentionally plain English.
        console.error(
          '[ShopifyProduct] Inventory sync failed:',
          inventoryFailures.map(f => ({ variantId: f.variantId, reason: f.reason, detail: f.detail })),
        );

        // All variants typically fail for the same root reason (token /
        // scope / location). Use the first reason for the headline; if
        // multiple distinct reasons appear they share the same generic
        // recovery action anyway.
        const friendly = mapInventoryFailureToFriendlyMessage(
          inventoryFailures[0]?.reason || 'shopify_rejected',
          inventoryFailures.length,
        );
        toast({
          title: friendly.title,
          description: friendly.description,
          variant: 'destructive',
          ...(friendly.offerReconnect
            ? {
                action: (
                  <ToastAction
                    altText="Reconnect Shopify"
                    onClick={() => navigate('/home/shopify')}
                  >
                    Reconnect Shopify
                  </ToastAction>
                ),
              }
            : {}),
        });
      } else {
        toast({ variant: "success", title: 'Product Updated', description: 'Changes synced to Shopify.' });
      }
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Update Failed' } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollableDialog open={open} onOpenChange={onOpenChange}>
      <ScrollableDialogContent className="max-w-3xl max-h-[90vh] p-0 overflow-hidden flex flex-col">

        {/* ── Header ────────────────────────────────────────────────────── */}
        <ScrollableDialogHeader className="px-6 py-4 border-b bg-white shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                <Package className="h-5 w-5" />
              </div>
              <div>
                <ScrollableDialogTitle className="text-base font-semibold">Edit Product</ScrollableDialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Changes will be synced to Shopify</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-8 text-xs border-slate-200 w-28 font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                    <SelectItem key={val} value={val} className="text-xs">
                      <div className="flex items-center gap-2">
                        <span className={cn('w-1.5 h-1.5 rounded-full', cfg.color)} />
                        {cfg.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </ScrollableDialogHeader>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={onSubmit} className="p-6 space-y-6">

            {/* Product Media */}
            <section>
              <SectionLabel icon={<ImageIcon className="h-4 w-4" />} title="Product Media" hint="First image is the primary thumbnail" />
              {mainImages.length === 0 ? (
                <DropZone dragging={dragging}
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={e => { e.preventDefault(); setDragging(false); addMainFiles(e.dataTransfer.files); }}
                  onClick={() => mainFileInputRef.current?.click()}>
                  <input ref={mainFileInputRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={e => addMainFiles(e.target.files)} />
                </DropZone>
              ) : (
                <div className={cn('rounded-xl border-2 border-dashed p-3 transition-colors',
                  dragging ? 'border-blue-400 bg-blue-50' : 'border-slate-200')}
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={e => { e.preventDefault(); setDragging(false); addMainFiles(e.dataTransfer.files); }}>
                  <div className="grid grid-cols-5 gap-2">
                    {mainImages.map((img, i) => (
                      <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                        <img src={img.preview} alt="" className="w-full h-full object-cover" />
                        {i === 0 && (
                          <div className="absolute top-1.5 left-1.5">
                            <Badge className="text-[9px] px-1.5 py-0 h-4 bg-black/70 text-white border-0 hover:bg-black/70">Primary</Badge>
                          </div>
                        )}
                        {img.base64 && (
                          <div className="absolute top-1.5 right-8">
                            <Badge className="text-[9px] px-1.5 py-0 h-4 bg-blue-500 text-white border-0">New</Badge>
                          </div>
                        )}
                        <button type="button"
                          onClick={() => setMainImages(prev => prev.filter((_, idx) => idx !== i))}
                          className="absolute top-1.5 right-1.5 w-5 h-5 bg-black/60 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => mainFileInputRef.current?.click()}
                      className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors group">
                      <Plus className="h-5 w-5 text-slate-400 group-hover:text-blue-500" />
                      <span className="text-[10px] text-slate-400 group-hover:text-blue-500 mt-1">Add</span>
                    </button>
                  </div>
                  <input ref={mainFileInputRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={e => addMainFiles(e.target.files)} />
                </div>
              )}
            </section>

            {/* Basic Info */}
            <section>
              <SectionLabel icon={<Package className="h-4 w-4" />} title="Basic Information" />
              <div className="space-y-4 bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div>
                  <Label className="text-xs font-medium text-slate-600">Product Title <span className="text-red-500">*</span></Label>
                  <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Product title" className="mt-1 bg-white" required />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600">Description</Label>
                  <Textarea value={description} onChange={e => setDescription(e.target.value)}
                    rows={3} placeholder="Product description…" className="mt-1 bg-white resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Vendor</Label>
                    {vendors.length > 0 ? (
                      <Select value={vendors.includes(vendor) ? vendor : '__custom__'} onValueChange={v => setVendor(v === '__custom__' ? vendor : v)}>
                        <SelectTrigger className="mt-1 bg-white h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {vendors.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                          <SelectItem value="__custom__">+ Enter custom…</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={vendor} onChange={e => setVendor(e.target.value)} placeholder={metaLoading ? 'Loading…' : 'Brand name'} className="mt-1 bg-white" />
                    )}
                    {(!vendors.includes(vendor) || vendor === '__custom__') && vendors.length > 0 && (
                      <Input value={vendor === '__custom__' ? '' : vendor} onChange={e => setVendor(e.target.value)}
                        placeholder="Enter vendor name" className="mt-2 bg-white" autoFocus />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Category / Type</Label>
                    {productTypes.length > 0 ? (
                      <Select value={productTypes.includes(productType) ? productType : (productType ? '__custom__' : '__none__')} onValueChange={v => setProductType(v === '__custom__' ? productType : v === '__none__' ? '' : v)}>
                        <SelectTrigger className="mt-1 bg-white h-9 text-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">None</SelectItem>
                          {productTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          <SelectItem value="__custom__">+ Enter custom…</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={productType} onChange={e => setProductType(e.target.value)} placeholder={metaLoading ? 'Loading…' : 'e.g. Clothing'} className="mt-1 bg-white" />
                    )}
                    {productType && !productTypes.includes(productType) && productTypes.length > 0 && (
                      <Input value={productType} onChange={e => setProductType(e.target.value)}
                        placeholder="Enter product type" className="mt-2 bg-white" />
                    )}
                  </div>
                </div>

                {/* Tag chip input */}
                <div>
                  <Label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                    <Tag className="h-3 w-3" /> Tags
                    <span className="font-normal text-slate-400">— press Enter or comma to add</span>
                  </Label>
                  <div className="mt-1 min-h-[38px] flex flex-wrap gap-1.5 items-center px-3 py-2 rounded-md border border-input bg-white cursor-text"
                    onClick={() => tagInputRef.current?.focus()}>
                    {tags.map((t, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs px-2 py-0.5">
                        {t}
                        <button type="button" onClick={() => setTags(prev => prev.filter((_, idx) => idx !== i))} className="hover:text-red-500 transition-colors">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <input ref={tagInputRef} value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={onTagKeyDown}
                      onBlur={() => tagInput.trim() && addTag(tagInput)}
                      placeholder={tags.length === 0 ? 'Add tags…' : ''}
                      className="flex-1 min-w-[100px] outline-none bg-transparent text-sm placeholder:text-muted-foreground" />
                  </div>
                </div>
              </div>
            </section>

            {/* Variants */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-slate-400" />
                  <h3 className="text-sm font-semibold text-slate-800">Variants</h3>
                  <Badge variant="outline" className="text-xs">{variants.length}</Badge>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addVariant} className="h-8 text-xs border-slate-200">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Variant
                </Button>
              </div>

              <div className="space-y-3">
                {variants.map((variant, index) => (
                  <div key={index} className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center">{index + 1}</span>
                        <span className="text-xs font-medium text-slate-600">{variant.option1 || `Variant ${index + 1}`}</span>
                        {variant.price && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">{currencySymbol}{variant.price}</Badge>}
                      </div>
                      {variants.length > 1 && (
                        <button type="button" onClick={() => removeVariant(index)}
                          className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    <div className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Variant image */}
                        <div className="shrink-0">
                          {variant.image ? (
                            <div className="relative group w-16 h-16 rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                              <img src={variant.image.preview} alt="Variant" className="w-full h-full object-cover" />
                              <button type="button" onClick={() => updateVariant(index, 'image', null)}
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div onClick={() => variantFileInputRefs.current[index]?.click()}
                              className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors group">
                              <ImageIcon className="h-5 w-5 text-slate-300 group-hover:text-blue-400" />
                              <span className="text-[9px] text-slate-400 group-hover:text-blue-500 mt-0.5">Image</span>
                            </div>
                          )}
                          <input type="file" accept="image/*" className="hidden"
                            ref={el => { variantFileInputRefs.current[index] = el; }}
                            onChange={e => setVariantImage(index, e.target.files)} />
                          {variant.image && (
                            <button type="button" onClick={() => variantFileInputRefs.current[index]?.click()}
                              className="text-[10px] text-blue-600 hover:underline mt-1 block text-center w-16">Change</button>
                          )}
                        </div>

                        {/* Fields */}
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Option</Label>
                            <Input value={variant.option1} onChange={e => updateVariant(index, 'option1', e.target.value)}
                              placeholder="S / Red / 1kg" className="mt-1 h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Price <span className="text-red-400">*</span></Label>
                            <div className="relative mt-1">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currencySymbol}</span>
                              <Input type="number" step="0.01" min="0" value={variant.price}
                                onChange={e => updateVariant(index, 'price', e.target.value)}
                                placeholder="0.00" className="h-9 text-sm pl-7" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Stock</Label>
                            <Input type="number" min="0" step="1" value={variant.inventory_quantity}
                              onChange={e => updateVariant(index, 'inventory_quantity',
                                e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                              placeholder="0" className="mt-1 h-9 text-sm" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </form>
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <ScrollableDialogFooter className="px-6 py-4 border-t bg-white shrink-0">
          <div className="flex items-center justify-between w-full">
            <p className="text-xs text-slate-400">
              {mainImages.length > 0 && `${mainImages.length} image${mainImages.length > 1 ? 's' : ''} · `}
              {variants.length} variant{variants.length > 1 ? 's' : ''}
              {tags.length > 0 && ` · ${tags.length} tag${tags.length > 1 ? 's' : ''}`}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
              <Button onClick={onSubmit} disabled={loading || !title.trim()} className="bg-blue-600 hover:bg-blue-700 text-white">
                {loading
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                  : <><Save className="mr-2 h-4 w-4" />Save Changes</>}
              </Button>
            </div>
          </div>
        </ScrollableDialogFooter>
      </ScrollableDialogContent>
    </ScrollableDialog>
  );
};

// ── Shared helpers ───────────────────────────────────────────────────────────

function SectionLabel({ icon, title, hint }: { icon: React.ReactNode; title: string; hint?: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-slate-400">{icon}</span>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

function DropZone({ dragging, onClick, onDragOver, onDragLeave, onDrop, children }: {
  dragging: boolean; onClick: () => void;
  onDragOver: React.DragEventHandler; onDragLeave: React.DragEventHandler; onDrop: React.DragEventHandler;
  children?: React.ReactNode;
}) {
  return (
    <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} onClick={onClick}
      className={cn('border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200',
        dragging ? 'border-blue-400 bg-blue-50 scale-[1.01]' : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50')}>
      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
        <Upload className="h-5 w-5 text-slate-400" />
      </div>
      <p className="text-sm font-medium text-slate-700">Drop images here or click to browse</p>
      <p className="text-xs text-slate-400 mt-1">PNG, JPG, WEBP · Max 20MB each · Multiple allowed</p>
      {children}
    </div>
  );
}

export default EditShopifyProductDialog;
